import React from 'react'

import { getProfilePath } from '@/common'
import Store, { StoreState } from '@/store/index'
import Session from '@/store/session'
import { ProfileImported, ProfileType } from '@/types/api'
import {
  AscendancyEmbed,
  AtlasEmbed,
  BuildListEmbed,
  CraftingEmbed,
  Embed,
  EquipmentEmbed,
  LinkEmbed,
  PantheonEmbed,
  PassivesEmbed,
  SkillsEmbed
} from '@/types/embed'
import { LevelingEmbed } from '@/types/leveling'
import { PlayerClass, Profile } from '@/types/profile'
import _ from 'lodash'

interface BaseEmbedSnapshot {
  initial: Embed
  type: Embed['type']
}
interface EquipmentEmbedSnapshot extends BaseEmbedSnapshot {
  type: 'equipment'
  items: EquipmentEmbed['items']
}
interface PantheonEmbedSnapshot extends BaseEmbedSnapshot {
  type: 'pantheon'
  active: PantheonEmbed['active']
}
interface SkillsEmbedSnapshot extends BaseEmbedSnapshot {
  type: 'skills' | 'skills-video-display'
  steps: SkillsEmbed['steps']
  step: number
}
interface LinkEmbedSnapshot extends BaseEmbedSnapshot {
  type: 'link'
  gems: LinkEmbed['gems']
}
interface AscendancyEmbedSnapshot extends BaseEmbedSnapshot {
  type: 'ascendancy'
  version: number
  ascendancy: string
  steps: AscendancyEmbed['steps']
}
interface PassivesEmbedSnapshot extends BaseEmbedSnapshot {
  type: 'passives'
  charClass: PlayerClass
  ascendancy?: string
  version: number
  variants: PassivesEmbed['variants']
  active: number
}
interface AtlasEmbedSnapshot extends BaseEmbedSnapshot {
  type: 'atlas'
  version: number
  variants: AtlasEmbed['variants']
  active: number
}
interface LevelingEmbedSnapshot extends BaseEmbedSnapshot {
  type: 'leveling'
  overview: LevelingEmbed['overview']
  steps: LevelingEmbed['steps']
  levelingSteps: LevelingEmbed['levelingSteps']
  tabs: LevelingEmbed['tabs']
  extraTabs: LevelingEmbed['extraTabs']
  milestones?: LevelingEmbed['milestones']
  pobLink?: string
  pobs?: LevelingEmbed['pobs']
  showLevelingSteps?: boolean
  showOverview?: boolean
  showProgression?: boolean
  showTipsAndTricks?: boolean
  tipsAndTricks?: LevelingEmbed['tipsAndTricks']
  skillGemsDescription?: LevelingEmbed['skillGemsDescription']
}
interface CraftingEmbedSnapshot extends BaseEmbedSnapshot {
  type: 'crafting'
  items: CraftingEmbed['items']
}
interface BuildListEmbedSnapshot extends BaseEmbedSnapshot {
  type: 'buildlist'
  builds: BuildListEmbed['builds']
}

type EmbedSnapshot =
  | EquipmentEmbedSnapshot
  | PantheonEmbedSnapshot
  | SkillsEmbedSnapshot
  | LinkEmbedSnapshot
  | AscendancyEmbedSnapshot
  | PassivesEmbedSnapshot
  | AtlasEmbedSnapshot
  | LevelingEmbedSnapshot
  | CraftingEmbedSnapshot
  | BuildListEmbedSnapshot

function embedSnapshot(embed: Embed): EmbedSnapshot {
  switch (embed.type) {
    case 'equipment':
      return {
        initial: embed,
        type: embed.type,
        items: embed.items
      }
    case 'pantheon':
      return {
        initial: embed,
        type: embed.type,
        active: embed.active
      }
    case 'skills':
    case 'skills-video-display':
      return {
        initial: embed,
        type: embed.type,
        steps: embed.steps,
        step: embed.step
      }
    case 'link':
      return {
        initial: embed,
        type: embed.type,
        gems: embed.gems
      }
    case 'ascendancy':
      return {
        initial: embed,
        type: embed.type,
        version: embed.version,
        ascendancy: embed.ascendancy,
        steps: embed.steps
      }
    case 'passives':
      return {
        initial: embed,
        type: embed.type,
        charClass: embed.charClass,
        ascendancy: embed.ascendancy,
        version: embed.version,
        variants: embed.variants,
        active: embed.active
      }
    case 'atlas':
      return {
        initial: embed,
        type: embed.type,
        version: embed.version,
        variants: embed.variants,
        active: embed.active
      }
    case 'leveling':
      return {
        initial: embed,
        type: embed.type,
        overview: embed.overview,
        steps: embed.steps,
        levelingSteps: embed.levelingSteps,
        tabs: embed.tabs,
        extraTabs: embed.extraTabs,
        milestones: embed.milestones,
        pobLink: embed.pobLink,
        pobs: embed.pobs,
        showLevelingSteps: embed.showLevelingSteps,
        showOverview: embed.showOverview,
        showProgression: embed.showProgression,
        showTipsAndTricks: embed.showTipsAndTricks,
        tipsAndTricks: embed.tipsAndTricks,
        skillGemsDescription: embed.skillGemsDescription
      }
    case 'crafting':
      return {
        initial: embed,
        type: embed.type,
        items: embed.items
      }
    case 'buildlist':
      return {
        initial: embed,
        type: embed.type,
        builds: embed.builds
      }
  }
}

function embedEquals(embed: Embed, snapshot: EmbedSnapshot): boolean {
  switch (embed.type) {
    case 'equipment':
      if (embed.type !== snapshot.type) return false
      if (embed.items !== snapshot.items) return false
      return true
    case 'pantheon':
      if (embed.type !== snapshot.type) return false
      if (embed.active !== snapshot.active) return false
      return true
    case 'skills':
    case 'skills-video-display':
      if (embed.type !== snapshot.type) return false
      if (embed.steps !== snapshot.steps) return false
      if (embed.step !== snapshot.step) return false
      return true
    case 'link':
      if (embed.type !== snapshot.type) return false
      if (embed.gems !== snapshot.gems) return false
      return true
    case 'ascendancy':
      if (embed.type !== snapshot.type) return false
      if (embed.version !== snapshot.version) return false
      if (embed.ascendancy !== snapshot.ascendancy) return false
      if (embed.steps !== snapshot.steps) return false
      return true
    case 'passives':
      if (embed.type !== snapshot.type) return false
      if (embed.charClass !== snapshot.charClass) return false
      if (embed.ascendancy !== snapshot.ascendancy) return false
      if (embed.version !== snapshot.version) return false
      if (embed.variants !== snapshot.variants) return false
      if (embed.active !== snapshot.active) return false
      return true
    case 'atlas':
      if (embed.type !== snapshot.type) return false
      if (embed.version !== snapshot.version) return false
      if (embed.variants !== snapshot.variants) return false
      if (embed.active !== snapshot.active) return false
      return true
    case 'leveling':
      if (embed.type !== snapshot.type) return false
      if (embed.overview !== snapshot.overview) return false
      if (embed.steps !== snapshot.steps) return false
      if (embed.levelingSteps !== snapshot.levelingSteps) return false
      if (embed.tabs !== snapshot.tabs) return false
      if (embed.extraTabs !== snapshot.extraTabs) return false
      if (embed.milestones !== snapshot.milestones) return false
      if (embed.pobLink !== snapshot.pobLink) return false
      if (embed.pobs !== snapshot.pobs) return false
      if (embed.showLevelingSteps !== snapshot.showLevelingSteps) return false
      if (embed.showOverview !== snapshot.showOverview) return false
      if (embed.showProgression !== snapshot.showProgression) return false
      if (embed.showTipsAndTricks !== snapshot.showTipsAndTricks) return false
      if (embed.tipsAndTricks !== snapshot.tipsAndTricks) return false
      if (embed.skillGemsDescription !== snapshot.skillGemsDescription) return false
      return true
    case 'crafting':
      if (embed.type !== snapshot.type) return false
      if (embed.items !== snapshot.items) return false
      return true
    case 'buildlist':
      if (embed.type !== snapshot.type) return false
      if (embed.builds !== snapshot.builds) return false
      return true
  }
}

type ProfileSnapshot = Omit<Profile, 'equipment' | 'skills' | 'passives' | 'atlas'> & {
  equipment: Profile['equipment']['variants']
  skills: Profile['skills']['steps']
  passives: Profile['passives']['variants']
  atlas: Profile['atlas']['variants']
  strategy: Profile['atlas']['strategy']
}

function profileSnapshot(profile: Profile): ProfileSnapshot {
  return {
    ..._.omit(profile, 'equipment', 'skills', 'passives', 'atlas'),
    equipment: profile.equipment.variants,
    skills: profile.skills.steps,
    passives: profile.passives.variants,
    atlas: profile.atlas.variants,
    strategy: profile.atlas.strategy
  }
}

const profileKeys: (keyof Profile)[] = [
  'class',
  'ascendancy',
  'level',
  'version',
  'showcaseVideo',
  'relatedBuild',
  'strAndWeak',
  'author',
  'skillRotations',
  'buildRating',
  'notes',
  'changelog',
  'lootfilterLinks',
  'pantheon',
  'bandits',
  'pobProfileId'
] as const

function profileEquals(profile: Profile, snapshot: ProfileSnapshot): boolean {
  for (const key of profileKeys) {
    if (profile[key] !== snapshot[key]) return false
  }
  if (profile.equipment.variants !== snapshot.equipment) return false
  if (profile.skills.steps !== snapshot.skills) return false
  if (profile.passives.variants !== snapshot.passives) return false
  if (profile.atlas.variants !== snapshot.atlas) return false
  if (profile.atlas.strategy !== snapshot.strategy) return false
  return true
}

type StateSnapshot = {
  initial: StoreState | undefined
  imported?: string
  metadata?: string
  embeds: EmbedSnapshot[]
  passive_tree?: EmbedSnapshot
  planner?: ProfileSnapshot
  items: StoreState['items']
}

function stateSnapshot(state: StoreState): StateSnapshot {
  return {
    initial: state,
    imported: state.imported?.id,
    metadata: state.imported?.metadata,
    embeds: state.embeds.map((embed) => embedSnapshot(embed)),
    passive_tree: state.passive_tree ? embedSnapshot(state.passive_tree) : undefined,
    planner: state.planner ? profileSnapshot(state.planner) : undefined,
    items: state.items
  }
}

function stateEquals(state: StoreState, snapshot: StateSnapshot) {
  if (state.imported?.metadata !== snapshot.metadata) return false
  if (state.embeds.length !== snapshot.embeds?.length) return false
  if (state.items !== snapshot.items) return false
  if (!state.embeds.every((embed, index) => embedEquals(embed, snapshot.embeds[index]))) return false
  if ((state.passive_tree != null) !== (snapshot.passive_tree != null)) return false
  if (state.passive_tree && !embedEquals(state.passive_tree, snapshot.passive_tree!)) return false
  if ((state.planner != null) !== (snapshot.planner != null)) return false
  if (state.planner && !profileEquals(state.planner, snapshot.planner!)) return false
  return true
}

function selectImported(state: StoreState, userId?: number): [ProfileImported | undefined, number | undefined] {
  if (state.imported && state.imported.modified !== 'heavy') {
    //const activeEmbed = state.embeds[state.activeEmbed];
    if (state.embeds[state.activeEmbed]) {
      return [state.imported, state.embeds[state.activeEmbed].id]
    } else {
      return [state.imported, undefined]
    }
  } else if (userId && state.imported?.user?.id === userId) {
    return [state.imported, undefined]
  } else {
    return [undefined, undefined]
  }
}

function identicalState(src: StoreState, dst: StoreState | undefined) {
  if (!dst) return false
  const keys = [...Object.keys(src), ...Object.keys(dst)] as (keyof StoreState)[]
  for (const key of keys) {
    if (key === 'imported') continue
    if (src[key] !== dst[key]) return false
  }
  return true
}

export function useImportId() {
  const userId = Session.useSelector((state) => state.user?.id)
  return Store.useSelector((state) => {
    const [imported] = selectImported(state, userId)
    return imported && imported.modified !== 'heavy' ? imported.id : undefined
  })
}

type Snapshot = StateSnapshot

type Snapshots = {
  [key in number]?: Snapshot
}

export function ImportTracker({ type, path }: { type: ProfileType; path?: string }) {
  const state = Store.useState()
  const dispatch = Store.useDispatch()

  const snapshotRef = React.useRef<Snapshots>({})

  React.useEffect(() => {
    const snapshots = snapshotRef.current
    const newSnapshots = (snapshotRef.current = {} as Snapshots)

    let modifiedState: ProfileImported['modified']
    if (state.imported && state.imported.modified !== 'heavy') {
      const snapshot = snapshots[state.id]
      if (!snapshot || !('embeds' in snapshot) || snapshot.imported !== state.imported?.id) {
        newSnapshots[state.id] = stateSnapshot(state)
      } else if (stateEquals(state, snapshot)) {
        newSnapshots[state.id] = snapshot
        if (state.imported?.modified !== 'light' && !identicalState(state, snapshot.initial)) {
          if (snapshot.initial) {
            modifiedState = 'light'
            snapshot.initial = undefined
          } else {
            snapshot.initial = state
          }
        }
      } else {
        modifiedState = 'heavy'
      }
    }

    if (modifiedState) {
      dispatch.setState((state) => {
        state = { ...state }
        if (state.imported && modifiedState) {
          state.imported = {
            ...state.imported,
            modified: modifiedState
          }
        }
        return state
      })
    }
  }, [state, dispatch])

  const userId = Session.useSelector((state) => state.user?.id)
  React.useEffect(() => {
    if (path) {
      const url = new URL(getProfilePath(path), window.location.href)
      if (window.location.search) url.search = window.location.search
      if (url.href !== window.location.href) {
        window.history?.replaceState(null, '', url.href)
      }
    } else {
      const [imported, id] = selectImported(state, userId)

      const importId = imported && imported.modified !== 'heavy' ? imported.id : undefined
      const url = new URL(getProfilePath(importId ?? '', type), window.location.href)
      if (window.location.search) url.search = window.location.search
      if (!importId && imported) {
        url.hash = '#' + imported.id
      } else if (id) {
        url.hash = '#' + id
      }
      if (url.href !== window.location.href) {
        window.history?.replaceState(null, '', url.href)
      }
    }
  }, [state, userId, type, path])

  return null
}
